// ============================================================================
// GuestLoginPage — Iter 62fh
// Route publique `/guest/:token` → échange le token contre un JWT, store
// localStorage, redirige vers /tasks. Si token invalide/expiré → écran erreur.
// ============================================================================
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

const NAVY = '#0B1330';
const BLUE = '#3E7BFA';
const BLUE_LIGHT = '#5BBEFC';
const T1 = '#F0F4FF';
const T3 = '#8A96B4';
const SURFACE = 'rgba(20, 29, 66, 0.62)';
const BORDER = 'rgba(50, 64, 120, 0.5)';

const inputCss = { width: '100%', padding: '12px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}`, color: T1, fontFamily: 'Sora', fontSize: 14, outline: 'none', boxSizing: 'border-box' };

export default function GuestLoginPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [state, setState] = useState('loading'); // loading | error
  const [errMsg, setErrMsg] = useState('');
  const [jwt, setJwt] = useState(null);
  const [guest, setGuest] = useState(null);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [saving, setSaving] = useState(false);
  const saveName = async (e) => {
    e.preventDefault();
    if (!first.trim()) return;
    setSaving(true);
    try {
      const r = await api.post('/guest/me/name', { first_name: first.trim(), last_name: last.trim() });
      setSession(jwt, r.data);
    } catch { /* on continue quand même */ }
    navigate('/tasks', { replace: true });
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.post('/auth/guest-login', { token });
        const { token: jwt, user } = r.data || {};
        if (!jwt || !user) throw new Error('Réponse serveur invalide');
        localStorage.removeItem('asa_impersonate_from');
        setSession(jwt, user);
        if (user.needs_name) { setJwt(jwt); setGuest(user); setState('name'); return; }
        setTimeout(() => navigate('/tasks', { replace: true }), 150);
      } catch (e) {
        localStorage.removeItem('asa_token');
        localStorage.removeItem('asa_guest_link');
        setState('error');
        setErrMsg(e?.response?.data?.detail || 'Ce lien ne fonctionne plus.');
      }
    })();
  }, [token, navigate, setSession]);

  return (
    <div style={{
      minHeight: '100vh', background: NAVY,
      display: 'grid', placeItems: 'center', padding: 20,
    }}>
      <div style={{
        width: '100%', maxWidth: 420,
        background: `linear-gradient(180deg, ${SURFACE} 0%, rgba(14,22,44,0.98) 100%)`,
        borderRadius: 22, border: `1px solid ${BORDER}`,
        padding: 32, textAlign: 'center',
        boxShadow: '0 40px 80px -20px rgba(0,0,0,0.6)',
      }}>
        {state === 'name' ? (
          <form onSubmit={saveName} data-testid="guest-name-onboarding">
            <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 21, color: T1, margin: 0 }}>Bienvenue sur ASA 👋</h2>
            <p style={{ fontFamily: 'Sora', fontSize: 13, color: T3, margin: '8px 0 20px', lineHeight: 1.5 }}>
              Tu es connecté en tant que <b style={{ color: T1 }}>{guest?.name}</b>{guest?.job_role && guest.job_role !== 'autre' ? ` (${guest.job_title?.replace('Invité · ', '')})` : ''}. Dis-nous comment tu t&apos;appelles.
            </p>
            <input data-testid="guest-first-name" autoFocus value={first} onChange={(e) => setFirst(e.target.value)} placeholder="Prénom *" style={inputCss} />
            <input data-testid="guest-last-name" value={last} onChange={(e) => setLast(e.target.value)} placeholder="Nom" style={{ ...inputCss, marginTop: 8 }} />
            <button type="submit" data-testid="guest-name-submit" disabled={!first.trim() || saving} style={{ marginTop: 18, width: '100%', padding: '12px 16px', borderRadius: 12, background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`, border: 'none', color: NAVY, fontFamily: 'Sora', fontWeight: 800, fontSize: 13, cursor: 'pointer', opacity: first.trim() ? 1 : 0.5 }}>{saving ? '…' : 'C\'est parti'}</button>
            <button type="button" data-testid="guest-name-skip" onClick={() => navigate('/tasks', { replace: true })} style={{ marginTop: 10, background: 'transparent', border: 'none', color: T3, fontFamily: 'Sora', fontSize: 12, cursor: 'pointer' }}>Plus tard</button>
          </form>
        ) : state === 'loading' ? (
          <>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              border: `3px solid ${BORDER}`, borderTopColor: BLUE_LIGHT,
              margin: '0 auto 20px', animation: 'spin 0.8s linear infinite',
            }} />
            <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 20, color: T1, margin: 0 }}>Accès invité…</h2>
            <p style={{ fontFamily: 'Sora', fontSize: 13, color: T3, marginTop: 8 }}>Chargement de ton espace Tâches</p>
          </>
        ) : (
          <>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(255,77,77,0.14)', border: '1px solid rgba(255,77,77,0.5)',
              display: 'grid', placeItems: 'center', color: '#FF7B7B',
              margin: '0 auto 18px', fontSize: 26, fontWeight: 800,
            }}>!</div>
            <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 20, color: T1, margin: 0 }}>Lien expiré</h2>
            <p style={{ fontFamily: 'Sora', fontSize: 13, color: T3, marginTop: 10, lineHeight: 1.5 }}>
              {errMsg}<br />Demande un nouveau lien à la personne qui t&apos;a invité.
            </p>

          </>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
